import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import type CK from '~/types/contentking';
import GraphQL from '~/types/graphql';

import Copy from '~/components/logic/Copy';
import Hint, {
	HintAttachment,
} from '~/components/patterns/hints/hint/Hint';
import InternalLink, {
	InternalLinkStyle,
} from '~/components/patterns/links/InternalLink';
import List, {
	ListSize,
} from '~/components/patterns/lists/List';
import PremiumFeatureSituation, {
	PremiumFeatureSituationStyle,
} from '~/components/app/PremiumFeatureSituation';
import SearchEngineActivityState, {
	SearchEngineActivityStateIconType,
	SearchEngineActivityStateStatus,
} from '~/components/patterns/statuses/SearchEngineActivityState';

import useLogFileAnalysisFeatureNudge from '~/hooks/useLogFileAnalysisFeatureNudge';

import matchAndReturn from '~/utilities/matchAndReturn';

import {
	notEmpty,
} from '~/utilities/typeCheck';



const messages = defineMessages({
	nudge: {
		id: 'ui.searchEngineActivityBadges.nudge',
	},
	seen: {
		id: 'ui.searchEngineActivityBadges.seen',
	},
	upsellAnnotation: {
		id: 'ui.searchEngineActivityBadges.upsellAnnotation',
	},
	visited: {
		id: 'ui.searchEngineActivityBadges.visited',
	},
});



export enum SearchEngineActivityBadgesWording {
	SeenBySearchEngine,
	VisitedBySearchEngine,
}



type Props = {
	isCompact?: boolean,
	searchEngineActivity: ReadonlyArray<{
		searchEngine: GraphQL.SearchEngineActivitySearchEngine,
		status: GraphQL.SearchEngineActivityAtMomentStatus,
	}> | null,
	showUpsellOrNudge?: boolean,
	showFeatureNotEnabled?: boolean,
	websiteId: CK.WebsiteId,
	wording?: SearchEngineActivityBadgesWording,
};

const SearchEngineActivityBadges: React.FC<Props> = (props) => {
	const {
		isCompact,
		searchEngineActivity,
		showUpsellOrNudge = false,
		showFeatureNotEnabled = true,
		websiteId,
		wording = SearchEngineActivityBadgesWording.VisitedBySearchEngine,
	} = props;

	if (showUpsellOrNudge && !searchEngineActivity) {
		return (
			<SearchEngineActivityBadgesUpsell websiteId={websiteId} />
		);
	}

	if (!searchEngineActivity) {
		return null;
	}

	const searchEngines = [
		GraphQL.SearchEngineActivitySearchEngine.Openai,
		GraphQL.SearchEngineActivitySearchEngine.Perplexity,
		GraphQL.SearchEngineActivitySearchEngine.Google,
		GraphQL.SearchEngineActivitySearchEngine.Bing,
	]

	const states = searchEngines.map((searchEngine) => {
		const status = (
			searchEngineActivity.find((activity) => activity.searchEngine === searchEngine)?.status
		) ?? 'unknown';

		if (status === 'featureNotEnabled' && !showFeatureNotEnabled) {
			return null;
		}

		const stateStatus = matchAndReturn(status, {
			visitedSince: SearchEngineActivityStateStatus.Visited,
			featureNotEnabled: SearchEngineActivityStateStatus.Unknown,
			notVisitedSince: SearchEngineActivityStateStatus.NotVisited,
			unknown: SearchEngineActivityStateStatus.Unknown,
		});

		return (
			<SearchEngineActivityState
				isCompact={isCompact ?? status === 'featureNotEnabled'}
				key={searchEngine}
				label={matchAndReturn(wording, {
					[SearchEngineActivityBadgesWording.VisitedBySearchEngine]: (
						<FormattedMessage
							{...messages.visited}
							values={{
								searchEngine,
								status,
							}}
						/>
					),
					[SearchEngineActivityBadgesWording.SeenBySearchEngine]: (
						<FormattedMessage
							{...messages.seen}
							values={{
								searchEngine,
								status,
							}}
						/>
					),
				})}
				searchEngineIconType={getSearchEngineIcon(searchEngine)}
				status={stateStatus}
			/>
		);
	}).filter(notEmpty);

	return (
		<List
			inline={true}
			size={ListSize.Small}
		>
			{states}
		</List>
	);
};



type SearchEngineActivityBadgesUpsellProps = {
	websiteId: CK.WebsiteId,
};

const SearchEngineActivityBadgesUpsell: React.FC<SearchEngineActivityBadgesUpsellProps> = (props) => {
	const {
		websiteId,
	} = props;

	const {
		isLogFileAnalysisEnabled,
		nudgeLogFileAnalysis,
		upsellLogFileAnalysis,
	} = useLogFileAnalysisFeatureNudge(websiteId);

	if (isLogFileAnalysisEnabled) {
		return null;
	}

	if (upsellLogFileAnalysis) {
		return (
			<PremiumFeatureSituation
				extraAnnotation={(
					<FormattedMessage {...messages.upsellAnnotation} />
				)}
				featureName={GraphQL.AccountFeature.LogFileAnalysis}
				style={PremiumFeatureSituationStyle.Tooltip}
			>
				{({ premiumAnnotation }) => {
					if (premiumAnnotation !== null) {
						return React.cloneElement(
							premiumAnnotation,
							{},
							(
								<List
									inline={true}
									size={ListSize.Small}
								>
									{Object.values(GraphQL.SearchEngineActivitySearchEngine).map((searchEngine) => (
										<SearchEngineActivityState
											isCompact={true}
											key={searchEngine}
											label={null}
											searchEngineIconType={getSearchEngineIcon(searchEngine)}
											status={SearchEngineActivityStateStatus.NotOnPlan}
										/>
									))}
								</List>
							),
						);
					}

					return null;
				}}
			</PremiumFeatureSituation>
		);
	} else if (nudgeLogFileAnalysis) {
		return (
			<Hint
				attachment={HintAttachment.Right}
				blurDelay={200}
				popup={(
					<Copy
						{...messages.nudge}
						values={{
							linkSettings: (chunks) => (
								<InternalLink
									routeName="website.detail.logFileAnalysis"
									routeParams={{ websiteId }}
									style={InternalLinkStyle.Decent}
								>
									{chunks}
								</InternalLink>
							),
						}}
					/>
				)}
				popupOffset={[0, 2]}
			>
				<List
					inline={true}
					size={ListSize.Small}
				>
					{Object.values(GraphQL.SearchEngineActivitySearchEngine).map((searchEngine) => (
						<SearchEngineActivityState
							isCompact={true}
							key={searchEngine}
							label={null}
							searchEngineIconType={getSearchEngineIcon(searchEngine)}
							status={SearchEngineActivityStateStatus.NotEnabled}
						/>
					))}
				</List>
			</Hint>
		);
	}

	return null;
}



function getSearchEngineIcon(searchEngine: GraphQL.SearchEngineActivitySearchEngine): SearchEngineActivityStateIconType {
	return matchAndReturn(searchEngine, {
		bing: SearchEngineActivityStateIconType.Bing,
		google: SearchEngineActivityStateIconType.Google,
		openai: SearchEngineActivityStateIconType.OpenAI,
		perplexity: SearchEngineActivityStateIconType.Perplexity,
	});
}



export default SearchEngineActivityBadges;
